// Shared engine for the @-mention card autocomplete used by deck
// notes (MarkdownEditor), friend chat (ChatView), and the in-game
// logbook chat (GameBoard). All three surfaces speak the same source
// syntax. `[Card Name](card:unique_id)`. So messages with mentions
// round-trip across persistence (server, cache, undo stack) with no
// per-surface escaping or parsing rules.

export const CARD_AUTOCOMPLETE_LIMIT = 8;

const CARD_LINK_PATTERN = /\[([^\]\n]+)\]\(card:([^)\s]+)\)/g;

// Caret-anchored "@query" detector. The query allows letters, numbers,
// apostrophes, hyphens, and a single trailing space so users can type
// multi-word card names ("Adept Illusionist") without the autocomplete
// closing on the space. Returns null when no live @-mention is open.
export function detectAtQuery(text, offset) {
  let i = offset - 1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === '@') {
      const before = i === 0 ? null : text[i - 1];
      // Trigger when @ stands at the start of a word. Anything non-
      // alphanumeric counts as a boundary. ("user@example" stays inert
      // because a letter precedes the @.)
      if (before === null || !/[A-Za-z0-9]/.test(before)) {
        return { atIndex: i, query: text.slice(i + 1, offset) };
      }
      return null;
    }
    if (ch === '\n') return null;
    if (offset - i > 60) return null;
    i--;
  }
  return null;
}

export function filterCards(sorceryCards, query) {
  if (!Array.isArray(sorceryCards)) return [];
  const q = String(query || '').trim().toLowerCase();
  if (!q) {
    return sorceryCards
      .filter((c) => c.name && c.unique_id)
      .slice(0, CARD_AUTOCOMPLETE_LIMIT);
  }
  const prefix = [];
  const contains = [];
  for (const card of sorceryCards) {
    if (!card.name || !card.unique_id) continue;
    const lower = card.name.toLowerCase();
    if (lower.startsWith(q)) prefix.push(card);
    else if (lower.includes(q)) contains.push(card);
    if (prefix.length >= CARD_AUTOCOMPLETE_LIMIT) break;
  }
  return [...prefix, ...contains].slice(0, CARD_AUTOCOMPLETE_LIMIT);
}

export function serializeCardMention(card) {
  return `[${card.name}](card:${card.unique_id})`;
}

// Split a message into text/card chunks for inline rendering. Each
// chunk is either `{ type: 'text', value }` or `{ type: 'card', name,
// cardId }`. Unmatched text. Including stray brackets. Stays in a
// text chunk verbatim.
export function parseCardMentions(text) {
  if (!text) return [];
  const chunks = [];
  let lastIndex = 0;
  for (const m of String(text).matchAll(CARD_LINK_PATTERN)) {
    if (m.index > lastIndex) {
      chunks.push({ type: 'text', value: text.slice(lastIndex, m.index) });
    }
    chunks.push({ type: 'card', name: m[1], cardId: m[2] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    chunks.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return chunks;
}

// Flatten card mentions to plain text. Replaces every `[Name](card:id)`
// with just `Name`. Used by message-preview surfaces (e.g. the friends
// sidebar's last-message snippet) where rendering the full pill isn't
// practical and the raw markdown would otherwise leak into the UI.
export function cardMentionsToPlainText(text) {
  if (!text) return '';
  return parseCardMentions(text)
    .map((chunk) => (chunk.type === 'card' ? chunk.name : chunk.value))
    .join('');
}

// Convert a viewport-space DOMRect (everything getBoundingClientRect
// returns) into canvas-local coordinates. `position: fixed` inside
// .app-canvas is scoped to it (transform: translateZ(0) makes the
// canvas a containing block), so popover anchors must be canvas-local.
export function rectToCanvasRect(viewportRect) {
  if (typeof document === 'undefined') {
    return { left: 0, top: 0, right: 0, bottom: 0, canvasWidth: 1600, canvasHeight: 900 };
  }
  const canvasEl = document.querySelector('.app-canvas');
  const canvas = canvasEl
    ? canvasEl.getBoundingClientRect()
    : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
  return {
    left: viewportRect.left - canvas.left,
    right: viewportRect.right - canvas.left,
    top: viewportRect.top - canvas.top,
    bottom: viewportRect.bottom - canvas.top,
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
  };
}
